import classNames from 'classnames';
import React from 'react';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { useGetCardInvoicesPaidRemains } from '../../../hooks/apolloHooks/useGetCardInvoicesPaidRemains';
import { CurrentCustomer } from '../../../integrations/allkort/graphql/interface/Customer';
import Link from '../../elements/Link';

type TransactionHeaderProps = {
	wrapperClasses?: string;
	customerData?: CurrentCustomer;
}

const TransactionHeader: React.FC<TransactionHeaderProps> = ({
	wrapperClasses = 'white-pale-section shadow-one',
	customerData
}) => {
	const creditCardNameConvetion = customerData?.isCompany ? 'företagskort' : 'tankkort';
	const creditCardNameConvetionDistinct = customerData?.isCompany ? 'företagskorten' : 'tankkorten';
	const remainingBalanceSmartCard = customerData?.balance?.remainingBalance;
	const creditLimitCreditCard = customerData?.balance?.remainingCredit;

	// Fetch ALL cards with automatic pagination (up to 200 cards)
	const { cards: allCards, loading: cardsLoading, error: cardsError } = useGetAllCards();

	// Fetch all paid/remains data with auto-pagination
	const { paidRemains } = useGetCardInvoicesPaidRemains();

	// Helper to check if an invoice is unpaid
	// An invoice is unpaid if: remains > 0 OR remains is null (unknown/unpaid)
	const isInvoiceUnpaid = (inv: any) => {
		return inv.remains === null || (inv.remains && inv.remains > 0);
	};

	// Get unpaid invoices and calculate total sum
	const unpaidInvoices = paidRemains.filter(isInvoiceUnpaid);
	const totalUnpaidSum = unpaidInvoices.reduce((sum, inv) => {
		const amount = inv.remains !== null ? inv.remains : 0;
		return sum + amount;
	}, 0);

	// Still loading cards?
	// TODO: handle loading state properly
	if (cardsError) return <div>Error loading cards: {cardsError.message}</div>

	// Filter cards by type (P = prepaid/smartcards, K = företagskort/tankkort/creditcards)
	// Only count non-disabled cards
	const hasActiveSmartCard = allCards.some((card: any) => card.type?.shortName === 'P' && card.limit?.disabled?.isDisabled !== true);
	const hasActiveCreditCard = allCards.some((card: any) => card.type?.shortName === 'K' && card.limit?.disabled?.isDisabled !== true);

	return (
		<div
			className={classNames(
				'Row__Inner',
				'transactions-header',
				wrapperClasses
			)}
		>
			<div className="transactions-overview">
				<div className="transactions-overview__header">
					<h1>Mina köp</h1>
					<p>Filtrera bland dina transaktioner genom korttyp eller datum.</p>
					{hasActiveCreditCard && !hasActiveSmartCard && (<p>Här kan du även se tillgänglig kredit på {creditCardNameConvetion}.</p>)}
					{hasActiveSmartCard && !hasActiveCreditCard && (<p>Här kan du även se ditt saldo på smartkorten.</p>)}
					{hasActiveCreditCard && hasActiveSmartCard && (<p>Här kan du även se ditt saldo på smartkorten samt tillgänglig kredit på {creditCardNameConvetionDistinct}.</p>)}
				</div>

				<div className={classNames(
					'transactions-overview__wrapper',
					hasActiveSmartCard && hasActiveCreditCard ? '' : 'one-card-type'
				)}>
					{ // Show smart card info if user has active smart card
						hasActiveSmartCard && (
							<div className="transactions-overview__smartcard-info">
								<p className="smaller-heading" role="heading" aria-level={4}>Saldo smartkort</p>
								<div className="remaining-balance">{remainingBalanceSmartCard} kr</div>
							</div>
						)
					}
					{ // Show credit card info if user has active credit card 
						hasActiveCreditCard && (
							<div className="transactions-overview__creditcard-info">
								<p className="smaller-heading" role="heading" aria-level={4}>Tillgänglig kredit {creditCardNameConvetion}</p>
								<div className="remaining-creditcard-limit">{creditLimitCreditCard} kr</div>
								<span className="divider-horizontal"></span>
								<p className="smaller-heading" role="heading" aria-level={4}>Obetalda fakturor</p>
								<div className="unpaid-invoices">
									<Link
										href="/mitt-konto/fakturor-kontobesked#invoices"
										ariaLabel="Se obetalda fakturor"
									>
										{totalUnpaidSum === 0 ? 'Inget att betala' : `${totalUnpaidSum.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr`}
									</Link>
								</div>
							</div>
						)
					}



				</div>
			</div>
		</div>
	)
}

export default TransactionHeader
